import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api";
import { Conversation, Door } from "../components/Door";
import { ControlsHelp, Modal, SettingsForm } from "../components/Dialogs";
import { HatsPanel } from "../components/HatsPanel";
import { Hud } from "../components/Hud";
import { InstructionsPanel } from "../components/InstructionsPanel";
import { PeoplePanel } from "../components/PeoplePanel";
import { ProcessesPanel } from "../components/ProcessesPanel";
import { AccountsPanel } from "../components/AccountsPanel";
import { SuppliersPanel } from "../components/SuppliersPanel";
import { InboxPanel } from "../components/InboxPanel";
import { DiaryPanel } from "../components/DiaryPanel";
import { PlanningPrompt } from "../components/PlanningPrompt";
import { HOLD_OPTION, unreadCount, type Tone } from "../email";
import { ScoringPanel } from "../components/Scoring";
import { Outcome } from "../components/Outcome";
import { Queue } from "../components/Queue";
import { IntroPanel, IntroScreen } from "../components/Intro";
import { PeopleProvider } from "../components/PersonRef";
import { hasSeenIntro, markIntroSeen, shouldShowIntro } from "../people";
import { WeekSummary } from "../components/WeekSummary";
import { focusFirstHeading, focusIsLost, useDocumentTitle } from "../a11y";
import { firstName, formatClock } from "../format";
import type { Settings } from "../settings";
import type { ObservedView, PlayerAction } from "../types";

export type Panel = "office" | "people" | "intro" | "hats" | "instructions" | "processes" | "diary" | "inbox" | "accounts" | "suppliers" | "scoring";

const NAV: { id: Panel; label: string; key?: string; later?: boolean }[] = [
  { id: "office", label: "Office", key: "O" },
  { id: "people", label: "People", key: "P" },
  { id: "intro", label: "Who's who", key: "W" },
  { id: "hats", label: "Hats", key: "H" },
  { id: "instructions", label: "Standing instructions", key: "S" },
  { id: "processes", label: "Processes", key: "R" },
  { id: "diary", label: "Diary", key: "D" },
  { id: "inbox", label: "Inbox", key: "I" },
  { id: "accounts", label: "Accounts", key: "A" },
  { id: "suppliers", label: "Suppliers", key: "U" },
];

const PANEL_TITLES: Record<Panel, string> = {
  office: "Office", people: "People", intro: "Who's who", hats: "Hats", instructions: "Standing instructions", processes: "Processes",
  diary: "Diary", inbox: "Inbox", accounts: "Accounts", suppliers: "Suppliers", scoring: "Scoring",
};

const PANEL_KEYS: Record<string, Panel> = { o: "office", p: "people", w: "intro", h: "hats", s: "instructions", r: "processes", d: "diary", i: "inbox", a: "accounts", u: "suppliers" };

interface LastChoice { to: string | null; text: string }

/** What the Continue button says (19 §6.2). The engine decides how far time actually runs. */
export function continueLabel(view: ObservedView): string {
  if (view.weekSummary) return view.clock.week >= view.clock.totalWeeks ? "Finish" : `Start week ${view.clock.week + 1}`;
  return "Continue";
}

function isTyping(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (el as HTMLElement).isContentEditable;
}

export function Game({ runId, settings, onSettings, onExit, onSignedOut, orgName }: {
  runId: string; settings: Settings; onSettings: (s: Partial<Settings>) => void; onExit: () => void; onSignedOut: () => void;
  /** For the "email me my results" panel on the outcome screen. */
  orgName: string;
}) {
  const [view, setView] = useState<ObservedView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panel>("office");
  // The last week's summary is shown before the outcome, like every other week's (19 §6.2).
  const [finalSummaryDone, setFinalSummaryDone] = useState(false);
  const [talking, setTalking] = useState(false);
  const [lastChoice, setLastChoice] = useState<LastChoice | null>(null);
  const [announce, setAnnounce] = useState("");
  const [help, setHelp] = useState(false);
  const [menu, setMenu] = useState(false);
  const [scoringRevealed, setScoringRevealed] = useState(false);
  const [confirmScoring, setConfirmScoring] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [introSeen, setIntroSeen] = useState(() => hasSeenIntro(runId));
  const [planningHidden, setPlanningHidden] = useState<number | null>(null);
  const continueRef = useRef<HTMLButtonElement>(null);
  const stageRef = useRef<HTMLElement>(null);
  const lastDoor = useRef<string | null>(null);
  const lastClock = useRef<string | null>(null);
  const lastSummary = useRef<number | null>(null);
  const seenEmails = useRef<Set<string> | null>(null);
  /** Set when the player moves to another panel, so focus follows them there (WCAG 2.4.3). */
  const moved = useRef(false);

  const fail = useCallback((e: unknown) => {
    if (e instanceof ApiError && e.status === 401) { onSignedOut(); return; }
    setError(e instanceof Error ? e.message : "Something went wrong.");
  }, [onSignedOut]);

  useEffect(() => {
    api.view(runId).then((r) => { setView(r.view); setScoringRevealed(r.scoringRevealed === true); }).catch(fail);
  }, [runId, fail]);

  // Announce politely (19 §1.7), in one message: the new time when the clock has moved, door arrivals and new
  // email. Return to the office when someone new arrives. Nothing is announced for the first view of a run.
  useEffect(() => {
    if (!view) return;
    const door = view.door;
    const id = door?.instanceId ?? null;
    const clock = formatClock(view.clock);
    const first = lastClock.current === null;
    const parts: string[] = [];
    if (!first && clock !== lastClock.current) parts.push(`${clock.replace(" · ", ", ")}.`);
    const summaryWeek = view.weekSummary?.week ?? null;
    if (!first && summaryWeek !== null && summaryWeek !== lastSummary.current) parts.push(`End of week ${summaryWeek}.`);
    lastSummary.current = summaryWeek;
    if (id && id !== lastDoor.current) {
      setTalking(false);
      if (!first) parts.push(door?.bringer ? `${door.bringer.name} is at your door: ${door.title}` : `New: ${door?.title ?? ""}`);
    }
    const ids = view.emails.map((e) => e.instanceId);
    const seen = seenEmails.current;
    if (seen) {
      const fresh = ids.filter((e) => !seen.has(e)).length;
      if (fresh > 0) parts.push(fresh === 1 ? "1 new email." : `${fresh} new emails.`);
    }
    seenEmails.current = new Set([...(seen ?? []), ...ids]);
    lastDoor.current = id;
    lastClock.current = clock;
    if (parts.length) setAnnounce(parts.join(" "));
  }, [view]);

  /** Somewhere sensible for focus when the control that had it has gone (an answered email, a finished week). */
  const recoverFocus = useCallback(() => {
    requestAnimationFrame(() => {
      if (!focusIsLost()) return;
      const stage = stageRef.current;
      const summary = stage?.querySelector<HTMLElement>(".summary");
      if (summary) { focusFirstHeading(summary); return; }
      const cont = continueRef.current;
      if (cont && !cont.disabled) { cont.focus(); return; }
      focusFirstHeading(stage?.querySelector<HTMLElement>(".stage__content") ?? stage ?? null);
    });
  }, []);

  const act = useCallback(async (action: PlayerAction) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.act(runId, action);
      setView(r.view);
      if (action.type === "advance") setLastChoice(null);
      return r.view;
    } catch (e) {
      fail(e);
      return undefined;
    } finally {
      setBusy(false);
      recoverFocus();
    }
  }, [busy, runId, fail, recoverFocus]);

  /** Several actions in order (bulk triage). Stops at the first failure and keeps the view from the last success. */
  const actMany = useCallback(async (actions: PlayerAction[]) => {
    if (busy || actions.length === 0) return undefined;
    setBusy(true);
    setError(null);
    let last: ObservedView | undefined;
    try {
      for (const a of actions) {
        last = (await api.act(runId, a)).view;
        setView(last);
      }
      return last;
    } catch (e) {
      fail(e);
      return undefined;
    } finally {
      setBusy(false);
      recoverFocus();
    }
  }, [busy, runId, fail, recoverFocus]);

  const choose = useCallback(async (optionId: string, tone?: Tone) => {
    const door = view?.door;
    if (!door) return;
    const text = optionId === HOLD_OPTION ? "It will have to wait. This is protected time." : door.options.find((o) => o.id === optionId)?.text ?? "";
    const next = await act({ type: "choose", instanceId: door.instanceId, optionId, ...(tone ? { tone } : {}) });
    if (next) {
      setTalking(false);
      setLastChoice({ to: door.bringer ? firstName(door.bringer.name) : null, text });
      if (!next.door) requestAnimationFrame(() => continueRef.current?.focus({ preventScroll: true }));
    }
  }, [view, act]);

  const reveal = useCallback(async () => {
    setRevealing(true);
    setError(null);
    try {
      await api.revealScoring(runId);
      setScoringRevealed(true);
      setConfirmScoring(false);
      moved.current = true;
      setPanel("scoring");
      setTalking(false);
    } catch (e) {
      setConfirmScoring(false);
      fail(e);
    } finally {
      setRevealing(false);
    }
  }, [runId, fail]);

  const advance = useCallback(() => {
    if (!view || view.door || view.run.ended) return;
    void act({ type: "advance" });
  }, [view, act]);

  // Keyboard (19 §6.4): single keys, ignored while typing, can be turned off.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || isTyping(document.activeElement)) return;
      if (document.querySelector("dialog[open]")) return;
      if (e.key === "Escape") {
        if (talking) setTalking(false);
        else if (panel !== "office") { moved.current = true; setPanel("office"); }
        return;
      }
      if (!settings.shortcuts || !view) return;
      if (e.key === "?") { setHelp(true); e.preventDefault(); return; }
      // Number keys in a conversation are handled by the Conversation itself (tones and the raid hold).
      if (talking && view.door && /^[1-9]$/.test(e.key)) return;
      const target = PANEL_KEYS[e.key.toLowerCase()];
      if (target) { moved.current = true; setPanel(target); setTalking(false); return; }
      // Enter only acts when focus is not on a control (otherwise the control handles it natively).
      const onControl = document.activeElement && document.activeElement !== document.body;
      if (e.key === "Enter" && !onControl && panel === "office") {
        e.preventDefault();
        if (view.door && !talking) setTalking(true);
        else if (!view.door) advance();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view, talking, panel, settings.shortcuts, choose, advance]);

  // When the player moves to another panel, take focus to its heading so they hear where they are. The door and the
  // conversation place focus themselves (on "See …" and the first response).
  useEffect(() => {
    if (!moved.current) return;
    moved.current = false;
    const content = stageRef.current?.querySelector<HTMLElement>(".stage__content");
    if (!content) return;
    stageRef.current?.scrollTo?.({ top: 0 });
    if (content.contains(document.activeElement) && document.activeElement !== content) return;
    if (panel === "office" && view?.door && !view.weekSummary && !view.run.ended) return;
    const prompt = panel === "office" ? content.querySelector<HTMLElement>(".planning") : null;
    focusFirstHeading(prompt ?? content, { preventScroll: true });
  });

  const panelTitle = PANEL_TITLES[panel];
  useDocumentTitle(view ? `${talking ? "Conversation" : panelTitle} · ${view.run.firmName}` : "Opening the office");

  if (!view) {
    return (
      <main className="entry">
        {error ? (
          <div className="entry__card">
            <p className="form__error" role="alert">{error}</p>
            <button type="button" className="btn" onClick={onExit}>Back</button>
          </div>
        ) : <p aria-live="polite">Opening the office…</p>}
      </main>
    );
  }

  if (shouldShowIntro(view, introSeen) && view.intro) {
    return (
      <IntroScreen intro={view.intro} title={view.run.scenarioTitle} firmName={view.run.firmName}
        onBegin={() => { markIntroSeen(runId); setIntroSeen(true); setPanel("office"); }} />
    );
  }

  const ended = view.run.ended;
  const main = (() => {
    if (panel === "people") return <PeoplePanel people={view.people} profiles={view.profiles} youId={view.you.id} />;
    if (panel === "intro") return <IntroPanel intro={view.intro} title={view.run.scenarioTitle} />;
    if (panel === "hats") return <HatsPanel hats={view.hats} people={view.people} />;
    if (panel === "instructions") {
      return <InstructionsPanel requests={view.requests} instructions={view.instructions} people={view.people} busy={busy}
        onAct={(a) => void act(a)} you={view.you} />;
    }
    if (panel === "processes") return <ProcessesPanel processes={view.processes} />;
    if (panel === "accounts") return <AccountsPanel accounts={view.accounts} />;
    if (panel === "suppliers") return <SuppliersPanel suppliers={view.suppliers ?? []} today={view.clock.date} youId={view.you.id} />;
    if (panel === "inbox") {
      return <InboxPanel view={view} busy={busy} shortcuts={settings.shortcuts} onAct={act} onActMany={actMany} />;
    }
    if (panel === "diary") return <DiaryPanel view={view} runId={runId} busy={busy} onAct={act} />;
    if (panel === "scoring" && scoringRevealed) return <ScoringPanel runId={runId} onSignedOut={onSignedOut} />;
    if (ended && view.weekSummary && !finalSummaryDone) {
      return (
        <WeekSummary summary={view.weekSummary} view={view}>
          <button ref={continueRef} type="button" className="btn btn--primary" onClick={() => setFinalSummaryDone(true)}>{continueLabel(view)} ▸</button>
        </WeekSummary>
      );
    }
    if (ended) return <Outcome runId={runId} onExit={onExit} orgName={orgName} />;
    if (view.weekSummary) {
      return (
        <WeekSummary summary={view.weekSummary} view={view}>
          <button ref={continueRef} type="button" className="btn btn--primary" disabled={busy} onClick={advance}>{continueLabel(view)} ▸</button>
          <button type="button" className="btn btn--quiet" onClick={onExit}>Save and stop here</button>
        </WeekSummary>
      );
    }
    if (view.door) {
      return talking
        ? <Conversation ev={view.door} busy={busy} shortcuts={settings.shortcuts} onChoose={(id, tone) => void choose(id, tone)} onBack={() => setTalking(false)} />
        : <Door ev={view.door} onSee={() => setTalking(true)} />;
    }
    return (
      <section className="door door--empty" aria-labelledby="door-empty-title">
        <div className="door__frame door__frame--empty" aria-hidden="true" />
        <div className="door__speech">
          <h2 id="door-empty-title" className="visually-hidden">Your door</h2>
          {lastChoice ? (
            <p className="last-choice">
              You{lastChoice.to ? ` told ${lastChoice.to}` : " said"}: <q>{lastChoice.text}</q>
            </p>
          ) : null}
          <p className="muted">No one is at your door.</p>
        </div>
      </section>
    );
  })();

  const unread = unreadCount(view.emails);
  const showPlanning = panel === "office" && !talking && !ended && !view.weekSummary
    && view.diary.planningDue && planningHidden !== view.clock.week;
  const goTo = (p: Panel) => { moved.current = true; setPanel(p); setTalking(false); };

  return (
    <PeopleProvider people={view.people} profiles={view.profiles}>
    <div className="shell" data-half={view.clock.half}>
      <a className="skip-link" href="#stage"
        onClick={(e) => { e.preventDefault(); focusFirstHeading(stageRef.current?.querySelector<HTMLElement>(".stage__content") ?? null); }}>
        Skip to the main content
      </a>
      <Hud view={view} onHelp={() => setHelp(true)} onMenu={() => setMenu(true)} />
      <nav className="nav" aria-label="Places">
        <p className="nav__firm">{view.run.firmName}</p>
        <ul>
          {NAV.map((n) => (
            <li key={n.id}>
              {n.later ? (
                <span className="nav__item nav__item--later" aria-disabled="true">{n.label} <span className="nav__later">later</span></span>
              ) : (
                <button type="button" className="nav__item" aria-current={panel === n.id ? "page" : undefined}
                  aria-keyshortcuts={settings.shortcuts && n.key ? n.key : undefined} onClick={() => goTo(n.id)}>
                  <span>
                    {n.label}
                    {n.id === "inbox" && unread > 0 && <> ({unread}<span className="visually-hidden"> unread</span>)</>}
                  </span>
                  {settings.shortcuts && n.key && <kbd className="nav__key" aria-hidden="true">{n.key}</kbd>}
                </button>
              )}
            </li>
          ))}
          {scoringRevealed && (
            <li>
              <button type="button" className="nav__item nav__item--scoring" aria-current={panel === "scoring" ? "page" : undefined}
                onClick={() => goTo("scoring")}>
                Scoring
              </button>
            </li>
          )}
        </ul>
      </nav>
      {/* tabIndex 0: the stage scrolls on its own, so keyboard users must be able to reach it to scroll it (2.1.1). */}
      <main className="stage" id="stage" ref={stageRef} tabIndex={0} aria-labelledby="stage-title">
        <h1 id="stage-title" className="visually-hidden">{view.run.firmName}: {talking ? "conversation" : panelTitle.toLowerCase()}</h1>
        {panel === "office" && !talking && <div className="window" aria-hidden="true" />}
        <div className="stage__content">
          {showPlanning && (
            <PlanningPrompt busy={busy} unread={unread} onGo={goTo}
              onDone={() => void act({ type: "planned" })}
              onDismiss={() => { setPlanningHidden(view.clock.week); recoverFocus(); }} />
          )}
          {main}
        </div>
        {/* Always present, so a new error is announced (a live region added with its text often is not). */}
        <div role="status" className={error ? "stage__error" : "visually-hidden"}>{error}</div>
      </main>
      <Queue items={view.queue} busy={busy} onAnswer={(instanceId, optionId, tone) => void act({ type: "choose", instanceId, optionId, ...(tone ? { tone } : {}) })} />
      <footer className="actionbar">
        <span className="actionbar__hint">
          {view.door && !ended && !view.weekSummary ? `${view.door.bringer ? firstName(view.door.bringer.name) : "Someone"} is waiting for you.` : ""}
        </span>
        {!ended && !view.weekSummary && (
          <button ref={view.door ? undefined : continueRef} type="button" className="btn btn--primary btn--continue"
            disabled={busy || view.door !== null} onClick={advance}>
            {continueLabel(view)} <span aria-hidden="true">▸</span>
          </button>
        )}
      </footer>
      <div className="visually-hidden" aria-live="polite" aria-atomic="true">{announce}</div>
      <Modal title="Controls" open={help} onClose={() => setHelp(false)}><ControlsHelp shortcuts={settings.shortcuts} /></Modal>
      <Modal title="Menu" open={menu} onClose={() => setMenu(false)}>
        <SettingsForm settings={settings} onChange={onSettings} />
        <div className="menu-intro">
          <button type="button" className="btn" onClick={() => { setMenu(false); goTo("intro"); }}>Who's who</button>
          <p className="muted">The introduction to this scenario: your role, how this works and the people you work with.</p>
        </div>
        <div className="menu-scoring">
          <h3>Scoring</h3>
          {scoringRevealed ? (
            <>
              <p className="muted">You have revealed how this run is scored.</p>
              <button type="button" className="btn" onClick={() => { setMenu(false); goTo("scoring"); }}>Open the scoring page</button>
            </>
          ) : (
            <>
              <p className="muted">How runs are scored is hidden while you play. It is always explained at the end.</p>
              <button type="button" className="btn" onClick={() => { setMenu(false); setConfirmScoring(true); }}>Scoring…</button>
            </>
          )}
        </div>
        <div className="row-actions">
          <button type="button" className="btn" onClick={() => { setMenu(false); onExit(); }}>Save and leave the office</button>
        </div>
      </Modal>
      <Modal title="See how you are scored?" open={confirmScoring} onClose={() => setConfirmScoring(false)}>
        <p>
          Seeing how you're scored can change how you play. Once revealed, it stays revealed for this run, and the run is
          marked in playtest results.
        </p>
        <div className="row-actions">
          <button type="button" className="btn" data-autofocus disabled={revealing} onClick={() => setConfirmScoring(false)}>Keep it hidden</button>
          <button type="button" className="btn btn--primary" disabled={revealing} onClick={() => void reveal()}>
            {revealing ? "Revealing…" : "Reveal scoring for this run"}
          </button>
        </div>
      </Modal>
      <aside className="small-screen" aria-label="Screen size"><p>{view.run.firmName} is best played on a laptop or tablet.</p></aside>
    </div>
    </PeopleProvider>
  );
}
